import { useState, useEffect, useRef } from 'react';
import { hrms, getFileUrl } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/common/Card';
import { theme } from '../../theme';

// ─── Shared style helpers ─────────────────────────────────────────────────────
const inputStyle = {
  width: '100%',
  padding: '0.55rem 0.75rem',
  border: `1.5px solid ${theme.border}`,
  borderRadius: 6,
  fontSize: '0.875rem',
  outline: 'none',
  boxSizing: 'border-box',
  color: theme.text,
  marginBottom: '0.75rem',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.78rem',
  fontWeight: 600,
  color: theme.text,
  marginBottom: '0.3rem',
};

const editBtnStyle = {
  background: '#F3F4F6',
  border: '1px solid #E5E7EB',
  borderRadius: 6,
  padding: '0.3rem 0.7rem',
  cursor: 'pointer',
  fontSize: '0.78rem',
  fontWeight: 600,
  color: theme.text,
};

const deleteBtnStyle = {
  color: '#DC2626',
  background: '#FEF2F2',
  border: '1px solid #FEE2E2',
  borderRadius: 6,
  padding: '0.3rem 0.7rem',
  cursor: 'pointer',
  fontSize: '0.78rem',
  fontWeight: 600,
};

const saveBtnStyle = {
  background: theme.primary,
  color: '#fff',
  border: 'none',
  borderRadius: 7,
  padding: '0.5rem 1.25rem',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '0.875rem',
};

const cancelBtnStyle = {
  background: '#F3F4F6',
  border: '1px solid #E5E7EB',
  borderRadius: 7,
  padding: '0.5rem 1.25rem',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '0.875rem',
  color: theme.text,
};

// ─── Row (read-only display) ──────────────────────────────────────────────────
function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', padding: '0.55rem 0', borderBottom: `1px solid ${theme.border}` }}>
      <span style={{ width: '210px', flexShrink: 0, fontSize: '0.8rem', color: theme.textMuted, fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: '0.875rem', color: theme.text, fontWeight: 500 }}>{value || '—'}</span>
    </div>
  );
}

// ─── Section header with optional edit button ─────────────────────────────────
function SectionHeader({ title, onEdit, addLabel, onAdd, action }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
      <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: theme.text }}>{title}</h3>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {action}
        {onAdd && (
          <button onClick={onAdd} style={editBtnStyle}>+ {addLabel || 'Add'}</button>
        )}
        {onEdit && (
          <button onClick={onEdit} style={editBtnStyle}>&#9998; Edit</button>
        )}
      </div>
    </div>
  );
}

// ─── Modal overlay ────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: '1.5rem', width: '100%',
        maxWidth: 500, maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: theme.text }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: theme.textMuted, lineHeight: 1 }}>&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Field helpers ────────────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <Field label={label}>
      <input type={type} value={value || ''} onChange={onChange} placeholder={placeholder}
        style={inputStyle} />
    </Field>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <Field label={label}>
      <select value={value || ''} onChange={onChange} style={inputStyle}>
        <option value="">— Select —</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </Field>
  );
}

function Textarea({ label, value, onChange, rows = 3, placeholder }) {
  return (
    <Field label={label}>
      <textarea value={value || ''} onChange={onChange} rows={rows} placeholder={placeholder}
        style={{ ...inputStyle, resize: 'vertical' }} />
    </Field>
  );
}

function ModalActions({ onCancel, onSave, saving }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
      <button onClick={onCancel} style={cancelBtnStyle} disabled={saving}>Cancel</button>
      <button onClick={onSave} style={saveBtnStyle} disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function MyProfile() {
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [history, setHistory] = useState({ education: [], skills: [], certifications: [], experience: [] });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');

  // modal open states
  const [modal, setModal] = useState(null); // 'personal'|'bank'|'nominee'|'edu-add'|'edu-edit'|'skill-add'|'exp-add'|'exp-edit'|'cert-add'

  // form states
  const [personalForm, setPersonalForm] = useState({});
  const [bankForm, setBankForm] = useState({});
  const [nomineeForm, setNomineeForm] = useState({});
  const [eduForm, setEduForm] = useState({});
  const [skillForm, setSkillForm] = useState({ skill_name: '', proficiency_level: 'beginner' });
  const [expForm, setExpForm] = useState({});
  const [certForm, setCertForm] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [changeRequests, setChangeRequests] = useState([]);
  const [changeReqSuccess, setChangeReqSuccess] = useState(false);
  const [identityForm, setIdentityForm] = useState({ aadhar_number: '', pan_number: '' });
  const [docUploading, setDocUploading] = useState(''); // field name being uploaded

  const photoInputRef = useRef(null);
  const resumeRef     = useRef(null);
  const aadharDocRef  = useRef(null);
  const panDocRef     = useRef(null);
  const passbookRef   = useRef(null);

  const reloadData = async () => {
    const [empRes, histRes, bankRes, crRes] = await Promise.allSettled([
      hrms.getEmployee(user.emp_id),
      hrms.getEmployeeHistory(user.emp_id),
      hrms.getMyBankAccounts(),
      hrms.getMyProfileChangeRequests(),
    ]);
    if (empRes.status === 'fulfilled') setEmp(empRes.value.data);
    if (histRes.status === 'fulfilled') {
      const h = histRes.value.data || {};
      setHistory({
        education: h.education || [],
        skills: h.skills || [],
        certifications: h.certifications || [],
        experience: h.experience || [],
      });
    }
    if (bankRes.status === 'fulfilled') setBankAccounts(bankRes.value.data || []);
    if (crRes.status === 'fulfilled') setChangeRequests(crRes.value.data || []);
  };

  useEffect(() => {
    if (!user?.emp_id) { setLoading(false); return; }
    reloadData().finally(() => setLoading(false));
  }, [user?.emp_id]); // eslint-disable-line

  if (loading) return <div style={{ padding: '2rem', color: theme.textMuted }}>Loading...</div>;
  if (!emp) return <div style={{ padding: '2rem', color: theme.textMuted }}>Profile not found.</div>;

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
  const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
  const toDateInput = (d) => d ? new Date(d).toISOString().slice(0, 10) : '';

  const closeModal = () => { setModal(null); setEditingId(null); setSaving(false); };

  // ── Photo upload ─────────────────────────────────────────────────────────────
  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('profile_photo', file);
    try {
      await hrms.saveProfileDraft(fd);
      await reloadData();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to upload photo.');
    }
    e.target.value = '';
  };

  // ── Personal Info ────────────────────────────────────────────────────────────
  const openPersonal = () => {
    setPersonalForm({
      father_husband_name: emp.father_husband_name || '',
      dob: toDateInput(emp.dob),
      gender: emp.gender || '',
      marital_status: emp.marital_status || '',
      blood_group: emp.blood_group || '',
      qualification: emp.qualification || '',
      phone: emp.phone || '',
      alternate_phone: emp.alternate_phone || '',
      personal_email: emp.personal_email || '',
      address: emp.address || '',
      present_address: emp.present_address || '',
    });
    setModal('personal');
  };

  const savePersonal = async () => {
    setSaving(true);
    try {
      await hrms.submitProfileChangeRequest(personalForm);
      await reloadData();
      closeModal();
      setChangeReqSuccess(true);
      setTimeout(() => setChangeReqSuccess(false), 5000);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to submit change request.');
      setSaving(false);
    }
  };

  // ── Identity Documents ───────────────────────────────────────────────────────
  const openEditIdentity = () => {
    setIdentityForm({ aadhar_number: emp.aadhar_number || '', pan_number: emp.pan_number || '' });
    setModal('identity');
  };
  const saveIdentity = async () => {
    setSaving(true);
    try {
      await hrms.submitProfileChangeRequest(identityForm);
      await reloadData();
      closeModal();
      setChangeReqSuccess(true);
      setTimeout(() => setChangeReqSuccess(false), 5000);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to submit change request.');
      setSaving(false);
    }
  };

  // ── Document Upload ───────────────────────────────────────────────────────────
  const uploadDoc = async (fieldName, file) => {
    if (!file) return;
    setDocUploading(fieldName);
    try {
      const fd = new FormData();
      fd.append(fieldName, file);
      await hrms.saveProfileDraft(fd);
      await reloadData();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Upload failed.');
    } finally {
      setDocUploading('');
    }
  };

  // ── Bank Accounts ─────────────────────────────────────────────────────────────
  const EMPTY_BANK = { bank_name: '', bank_branch: '', ifsc_code: '', account_number: '', account_type: 'savings', account_holder: '', is_primary: false };

  const openAddBank = () => { setBankForm(EMPTY_BANK); setEditingId(null); setModal('bank'); };
  const openEditBank = (b) => {
    setBankForm({ bank_name: b.bank_name || '', bank_branch: b.bank_branch || '', ifsc_code: b.ifsc_code || '', account_number: b.account_number || '', account_type: b.account_type || 'savings', account_holder: b.account_holder || '', is_primary: b.is_primary || false });
    setEditingId(b.id);
    setModal('bank');
  };

  const saveBank = async () => {
    if (!bankForm.bank_name || !bankForm.account_number) { alert('Bank name and account number are required.'); return; }
    setSaving(true);
    try {
      if (editingId) await hrms.updateBankAccount(user.emp_id, editingId, bankForm);
      else await hrms.addBankAccount(user.emp_id, bankForm);
      await reloadData();
      closeModal();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save.');
      setSaving(false);
    }
  };

  const deleteBank = async (bid) => {
    if (!window.confirm('Remove this bank account?')) return;
    try { await hrms.deleteBankAccount(user.emp_id, bid); await reloadData(); }
    catch { alert('Failed to delete.'); }
  };

  const setPrimary = async (bid) => {
    try { await hrms.updateBankAccount(user.emp_id, bid, { is_primary: true }); await reloadData(); }
    catch { alert('Failed to update.'); }
  };

  // ── Nominee Details ──────────────────────────────────────────────────────────
  const openNominee = () => {
    setNomineeForm({
      nominee_name: emp.nominee_name || '',
      nominee_relation: emp.nominee_relation || '',
      nominee_aadhar: emp.nominee_aadhar || '',
      nominee_dob: toDateInput(emp.nominee_dob),
      nominee_contact: emp.nominee_contact || '',
    });
    setModal('nominee');
  };

  const saveNominee = async () => {
    setSaving(true);
    try {
      await hrms.saveProfileDraft(nomineeForm);
      await reloadData();
      closeModal();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save.');
      setSaving(false);
    }
  };

  // ── Education ────────────────────────────────────────────────────────────────
  const openAddEdu = () => {
    setEduForm({ degree: '', field_of_study: '', institution: '', start_year: '', end_year: '', grade: '' });
    setEditingId(null);
    setModal('edu');
  };

  const openEditEdu = (item) => {
    setEduForm({
      degree: item.degree || '',
      field_of_study: item.field_of_study || '',
      institution: item.institution || '',
      start_year: item.start_year || '',
      end_year: item.end_year || '',
      grade: item.grade || '',
    });
    setEditingId(item.id);
    setModal('edu');
  };

  const saveEdu = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await hrms.updateEducation(user.emp_id, editingId, eduForm);
      } else {
        await hrms.addEducation(user.emp_id, eduForm);
      }
      await reloadData();
      closeModal();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save.');
      setSaving(false);
    }
  };

  const deleteEdu = async (id) => {
    if (!window.confirm('Delete this education record?')) return;
    try {
      await hrms.deleteEducation(user.emp_id, id);
      await reloadData();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete.');
    }
  };

  // ── Skills ───────────────────────────────────────────────────────────────────
  const openAddSkill = () => {
    setSkillForm({ skill_name: '', proficiency_level: 'beginner' });
    setModal('skill');
  };

  const saveSkill = async () => {
    if (!skillForm.skill_name.trim()) { alert('Skill name is required.'); return; }
    setSaving(true);
    try {
      await hrms.addSkill(user.emp_id, skillForm);
      await reloadData();
      closeModal();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save.');
      setSaving(false);
    }
  };

  const deleteSkill = async (id) => {
    if (!window.confirm('Remove this skill?')) return;
    try {
      await hrms.deleteSkill(user.emp_id, id);
      await reloadData();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete.');
    }
  };

  // ── Work Experience ──────────────────────────────────────────────────────────
  const openAddExp = () => {
    setExpForm({ company_name: '', designation: '', from_date: '', to_date: '', description: '' });
    setEditingId(null);
    setModal('exp');
  };

  const openEditExp = (item) => {
    setExpForm({
      company_name: item.company_name || '',
      designation: item.designation || '',
      from_date: toDateInput(item.from_date),
      to_date: toDateInput(item.to_date),
      description: item.description || '',
    });
    setEditingId(item.id);
    setModal('exp');
  };

  const saveExp = async () => {
    setSaving(true);
    try {
      const payload = { ...expForm };
      if (!payload.to_date) delete payload.to_date;
      if (editingId) {
        await hrms.updateExperience(user.emp_id, editingId, payload);
      } else {
        await hrms.addExperience(user.emp_id, payload);
      }
      await reloadData();
      closeModal();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save.');
      setSaving(false);
    }
  };

  const deleteExp = async (id) => {
    if (!window.confirm('Delete this experience record?')) return;
    try {
      await hrms.deleteExperience(user.emp_id, id);
      await reloadData();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete.');
    }
  };

  // ── Certifications ───────────────────────────────────────────────────────────
  const openAddCert = () => {
    setCertForm({ name: '', issuing_org: '', issue_date: '', expiry_date: '' });
    setModal('cert');
  };

  const saveCert = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', certForm.name || '');
      fd.append('issuing_org', certForm.issuing_org || '');
      if (certForm.issue_date) fd.append('issue_date', certForm.issue_date);
      if (certForm.expiry_date) fd.append('expiry_date', certForm.expiry_date);
      await hrms.addCertification(user.emp_id, fd);
      await reloadData();
      closeModal();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save.');
      setSaving(false);
    }
  };

  const deleteCert = async (id) => {
    if (!window.confirm('Delete this certification?')) return;
    try {
      await hrms.deleteCertification(user.emp_id, id);
      await reloadData();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete.');
    }
  };

  // ─── Experience duration helper ───────────────────────────────────────────────
  const expDuration = (from, to) => {
    if (!from) return '';
    const start = new Date(from);
    const end = to ? new Date(to) : new Date();
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const yrs = Math.floor(months / 12);
    const mos = months % 12;
    return yrs > 0 ? `${yrs} yr${yrs > 1 ? 's' : ''}${mos > 0 ? ` ${mos} mo` : ''}` : `${mos} mo`;
  };

  const TABS = [
    { key: 'profile', label: 'Profile' },
    { key: 'career', label: 'Education & Career' },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: '900px' }}>

      {/* ── Profile Header ────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', marginBottom: '1.5rem' }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', overflow: 'hidden',
            background: theme.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '2rem', fontWeight: 700, color: theme.primaryDark, border: `3px solid ${theme.border}`,
            cursor: 'pointer',
          }} onClick={() => photoInputRef.current?.click()}>
            {emp.profile_photo
              ? <img src={getFileUrl(emp.profile_photo)} alt={emp.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : (emp.full_name ? emp.full_name.charAt(0).toUpperCase() : 'E')}
          </div>
          <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={handlePhotoChange} />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: theme.text, margin: 0 }}>{emp.full_name}</h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.2rem 0 0.5rem' }}>
            {emp.position_name || '—'}
            {emp.emp_code ? ` · ${emp.emp_code}` : ''}
            {emp.department_name ? ` · ${emp.department_name}` : ''}
          </p>
          <button onClick={() => photoInputRef.current?.click()} style={{ ...editBtnStyle, fontSize: '0.75rem' }}>
            Change Photo
          </button>
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', borderBottom: `2px solid ${theme.border}` }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '0.625rem 1.25rem', fontSize: '0.9rem', fontWeight: 600,
            color: activeTab === t.key ? theme.primary : theme.textMuted,
            borderBottom: activeTab === t.key ? `2px solid ${theme.primary}` : '2px solid transparent',
            marginBottom: '-2px', transition: 'color 0.15s',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══════════════════ PROFILE TAB ══════════════════════════════════════ */}
      {activeTab === 'profile' && (
        <>
          {/* Change request success banner */}
          {changeReqSuccess && (
            <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.1rem' }}>✓</span>
              <div>
                <p style={{ margin: 0, fontWeight: 600, color: '#065F46', fontSize: '0.875rem' }}>Change request submitted</p>
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#047857' }}>Your profile changes have been submitted for HR Admin approval.</p>
              </div>
            </div>
          )}

          {/* Personal Information */}
          <Card style={{ marginBottom: '1.25rem' }}>
            <SectionHeader title="Personal Information" onEdit={openPersonal} />
            <Row label="Display Name" value={emp.full_name} />
            <Row label="Name as per Aadhaar" value={emp.name_as_per_aadhar} />
            <Row label="Father / Husband Name" value={emp.father_husband_name} />
            <Row label="Date of Birth" value={fmt(emp.dob)} />
            <Row label="Gender" value={cap(emp.gender)} />
            <Row label="Marital Status" value={cap(emp.marital_status)} />
            <Row label="Blood Group" value={emp.blood_group} />
            <Row label="Qualification" value={emp.qualification} />
            <Row label="Primary Contact" value={emp.phone} />
            <Row label="Alternate Contact" value={emp.alternate_phone} />
            <Row label="Personal Email" value={emp.personal_email} />
            <Row label="Permanent Address" value={emp.address} />
            <Row label="Present Address" value={emp.present_address} />
          </Card>

          {/* Employment Details — read only */}
          <Card style={{ marginBottom: '1.25rem' }}>
            <SectionHeader title="Employment Details" />
            <Row label="Employee Code" value={emp.emp_code} />
            <Row label="Department" value={emp.department_name} />
            <Row label="Team" value={emp.team_name} />
            <Row label="Position" value={emp.position_name} />
            <Row label="Employment Type" value={cap(emp.employment_type?.replace('_', ' '))} />
            <Row label="Employment Status" value={cap(emp.employment_status)} />
            <Row label="Date of Joining" value={fmt(emp.hire_date)} />
            <Row label="Reporting Manager" value={emp.reporting_manager_name} />
          </Card>

          {/* Bank Accounts */}
          <Card style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700 }}>Bank Accounts</h3>
              <button onClick={openAddBank} style={{ ...editBtnStyle, background: theme.primary, color: '#fff', border: 'none' }}>+ Add Account</button>
            </div>
            {bankAccounts.length === 0 ? (
              <p style={{ color: theme.textMuted, fontSize: '0.875rem', textAlign: 'center', padding: '1rem 0' }}>No bank accounts added yet.</p>
            ) : bankAccounts.map((b) => (
              <div key={b.id} style={{ border: `1.5px solid ${b.is_primary ? theme.primary : theme.border}`, borderRadius: 8, padding: '0.875rem 1rem', marginBottom: '0.75rem', position: 'relative', background: b.is_primary ? '#EEF2FF' : '#fff' }}>
                {b.is_primary && (
                  <span style={{ position: 'absolute', top: -10, left: 12, background: theme.primary, color: '#fff', fontSize: '0.6rem', fontWeight: 800, padding: '0.15rem 0.5rem', borderRadius: 9999, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Primary</span>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem 1rem', fontSize: '0.85rem' }}>
                  <div><span style={{ color: theme.textMuted, fontSize: '0.75rem' }}>Bank</span><p style={{ margin: 0, fontWeight: 600 }}>{b.bank_name}</p></div>
                  <div><span style={{ color: theme.textMuted, fontSize: '0.75rem' }}>Account Number</span><p style={{ margin: 0, fontWeight: 600 }}>{b.account_number}</p></div>
                  <div><span style={{ color: theme.textMuted, fontSize: '0.75rem' }}>IFSC</span><p style={{ margin: 0 }}>{b.ifsc_code || '—'}</p></div>
                  <div><span style={{ color: theme.textMuted, fontSize: '0.75rem' }}>Branch</span><p style={{ margin: 0 }}>{b.bank_branch || '—'}</p></div>
                  {b.account_holder && <div><span style={{ color: theme.textMuted, fontSize: '0.75rem' }}>Account Holder</span><p style={{ margin: 0 }}>{b.account_holder}</p></div>}
                  <div><span style={{ color: theme.textMuted, fontSize: '0.75rem' }}>Type</span><p style={{ margin: 0, textTransform: 'capitalize' }}>{b.account_type}</p></div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', justifyContent: 'flex-end' }}>
                  {!b.is_primary && <button onClick={() => setPrimary(b.id)} style={{ ...editBtnStyle, color: theme.primary, borderColor: theme.primary }}>Set Primary</button>}
                  <button onClick={() => openEditBank(b)} style={editBtnStyle}>✏ Edit</button>
                  <button onClick={() => deleteBank(b.id)} style={deleteBtnStyle}>🗑 Remove</button>
                </div>
              </div>
            ))}
          </Card>

          {/* Nominee Details */}
          <Card style={{ marginBottom: '1.25rem' }}>
            <SectionHeader title="Nominee Details" onEdit={openNominee} />
            <Row label="Nominee Name" value={emp.nominee_name} />
            <Row label="Relationship" value={emp.nominee_relation} />
            <Row label="Nominee Aadhar" value={emp.nominee_aadhar} />
            <Row label="Nominee Date of Birth" value={fmt(emp.nominee_dob)} />
            <Row label="Nominee Contact" value={emp.nominee_contact} />
          </Card>

          {/* Identity Documents */}
          <Card style={{ marginBottom: '1.25rem' }}>
            <SectionHeader title="Identity Documents" onEdit={openEditIdentity} />
            <Row label="Aadhar Number" value={emp.aadhar_number} />
            <Row label="PAN Number" value={emp.pan_number} />
          </Card>

          {/* Documents */}
          <Card style={{ marginBottom: '1.25rem' }}>
            <SectionHeader title="Documents" />
            {[
              { field: 'resume',        ref: resumeRef,    label: 'Resume',           path: emp.resume_path },
              { field: 'aadhar_copy',   ref: aadharDocRef, label: 'Aadhar Card Copy', path: emp.aadhar_copy_path },
              { field: 'pan_copy',      ref: panDocRef,    label: 'PAN Card Copy',    path: emp.pan_copy_path },
              { field: 'passbook_copy', ref: passbookRef,  label: 'Bank Passbook',    path: emp.passbook_copy_path },
            ].map(({ field, ref, label, path }) => (
              <div key={field} style={{ display: 'flex', padding: '0.5rem 0', borderBottom: `1px solid ${theme.border}`, alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ width: '180px', flexShrink: 0, fontSize: '0.8rem', color: theme.textMuted, fontWeight: 500 }}>{label}</span>
                <div style={{ flex: 1 }}>
                  {path
                    ? <a href={getFileUrl(path)} target="_blank" rel="noreferrer" style={{ fontSize: '0.875rem', color: theme.primary, fontWeight: 500, textDecoration: 'none' }}>View / Download ↗</a>
                    : <span style={{ fontSize: '0.8rem', color: theme.textMuted }}>Not uploaded</span>
                  }
                </div>
                <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                  onChange={e => { uploadDoc(field, e.target.files?.[0]); e.target.value = ''; }} />
                <button onClick={() => ref.current?.click()} disabled={docUploading === field}
                  style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, borderRadius: '0.375rem', border: `1px solid ${theme.border}`, background: '#fff', cursor: 'pointer', color: theme.text, opacity: docUploading === field ? 0.5 : 1, whiteSpace: 'nowrap' }}>
                  {docUploading === field ? 'Uploading…' : path ? 'Replace' : 'Upload'}
                </button>
              </div>
            ))}
          </Card>

          {/* PF & ESI — read only */}
          {(emp.pf_uan || emp.esic_number) && (
            <Card style={{ marginBottom: '1.25rem' }}>
              <SectionHeader title="PF & ESI" />
              {emp.pf_uan && <Row label="PF UAN" value={emp.pf_uan} />}
              {emp.esic_number && <Row label="ESI Number" value={emp.esic_number} />}
              {emp.esi_hospital && <Row label="ESI Hospital" value={emp.esi_hospital} />}
            </Card>
          )}

          {/* Pending Change Requests */}
          {changeRequests.length > 0 && (
            <Card style={{ marginBottom: '1.25rem' }}>
              <SectionHeader title="Profile Change Requests" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {changeRequests.map((r) => {
                  const statusMap = { pending: { label: 'Pending', color: '#92400E', bg: '#FEF3C7' }, approved: { label: 'Approved', color: '#065F46', bg: '#D1FAE5' }, rejected: { label: 'Rejected', color: '#991B1B', bg: '#FEE2E2' } };
                  const s = statusMap[r.status] || statusMap.pending;
                  return (
                    <div key={r.id} style={{ padding: '0.75rem 1rem', border: `1px solid ${theme.border}`, borderRadius: '0.5rem', background: theme.pageBg }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.35rem' }}>
                        <span style={{ fontSize: '0.78rem', color: theme.textMuted }}>{new Date(r.requested_at).toLocaleDateString('en-IN')}</span>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, background: s.bg, color: s.color, padding: '0.1rem 0.45rem', borderRadius: '0.25rem' }}>{s.label}</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                        {Object.keys(r.field_changes).map(k => (
                          <span key={k} style={{ fontSize: '0.72rem', background: theme.lightBg, color: theme.primaryDark, padding: '0.1rem 0.4rem', borderRadius: '0.25rem', border: `1px solid ${theme.primaryLight}` }}>
                            {k.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                      {r.remarks && <p style={{ margin: '0.35rem 0 0', fontSize: '0.78rem', color: theme.danger }}>Note: {r.remarks}</p>}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </>
      )}

      {/* ══════════════════ EDUCATION & CAREER TAB ═══════════════════════════ */}
      {activeTab === 'career' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          {/* Education */}
          <Card>
            <SectionHeader title="Education" onAdd={openAddEdu} addLabel="Add Education" />
            {history.education.length === 0
              ? <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0', margin: 0 }}>No education records added yet.</p>
              : history.education.map((e) => (
                <div key={e.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.75rem 0', borderBottom: `1px solid ${theme.border}` }}>
                  <div style={{ width: 36, height: 36, borderRadius: '0.5rem', background: '#EFF6FF', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>
                    &#127891;
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: '0.875rem', color: theme.text }}>
                      {e.degree}{e.field_of_study ? ` — ${e.field_of_study}` : ''}
                    </p>
                    <p style={{ margin: '0.15rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>
                      {e.institution}{e.end_year ? ` · ${e.end_year}` : ''}
                      {e.grade ? ` · Grade: ${e.grade}` : ''}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                    <button onClick={() => openEditEdu(e)} style={editBtnStyle}>&#9998;</button>
                    <button onClick={() => deleteEdu(e.id)} style={deleteBtnStyle}>&#10005;</button>
                  </div>
                </div>
              ))}
          </Card>

          {/* Skills */}
          <Card>
            <SectionHeader title="Skills" onAdd={openAddSkill} addLabel="Add Skill" />
            {history.skills.length === 0
              ? <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0', margin: 0 }}>No skills added yet.</p>
              : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', paddingTop: '0.25rem' }}>
                  {history.skills.map((s) => (
                    <span key={s.id} style={{
                      background: theme.primaryLight, color: theme.primaryDark,
                      padding: '0.3rem 0.875rem', borderRadius: '9999px', fontSize: '0.8rem',
                      fontWeight: 600, border: `1px solid ${theme.primary}30`,
                      display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                    }}>
                      {s.skill_name} · {s.proficiency_level}
                      <button onClick={() => deleteSkill(s.id)} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: theme.primaryDark, fontWeight: 700, fontSize: '0.85rem',
                        padding: 0, lineHeight: 1, marginLeft: '0.1rem',
                      }}>&times;</button>
                    </span>
                  ))}
                </div>
              )}
          </Card>

          {/* Work Experience */}
          <Card>
            <SectionHeader title="Work Experience" onAdd={openAddExp} addLabel="Add Experience" />
            {history.experience.length === 0
              ? <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0', margin: 0 }}>No work experience records added yet.</p>
              : (
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: 19, top: 24, bottom: 8, width: 2, background: theme.border }} />
                  {history.experience.map((exp) => {
                    const fromStr = exp.from_date ? new Date(exp.from_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '';
                    const toStr = exp.to_date ? new Date(exp.to_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'Present';
                    const dur = expDuration(exp.from_date, exp.to_date);
                    return (
                      <div key={exp.id} style={{ display: 'flex', gap: '0.875rem', paddingBottom: '1.25rem', position: 'relative' }}>
                        <div style={{ width: 40, height: 40, borderRadius: '0.5rem', background: '#EFF6FF', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0, zIndex: 1 }}>
                          &#127970;
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: 0, fontWeight: 700, fontSize: '0.9rem', color: theme.text }}>{exp.designation}</p>
                          <p style={{ margin: '0.1rem 0 0', fontWeight: 600, fontSize: '0.82rem', color: theme.primary }}>{exp.company_name}</p>
                          <p style={{ margin: '0.1rem 0 0', fontSize: '0.77rem', color: theme.textMuted }}>
                            {fromStr}{fromStr ? ' – ' : ''}{toStr}{dur ? ` · ${dur}` : ''}
                          </p>
                          {exp.description && (
                            <p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: theme.gray, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{exp.description}</p>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, alignSelf: 'flex-start' }}>
                          <button onClick={() => openEditExp(exp)} style={editBtnStyle}>&#9998;</button>
                          <button onClick={() => deleteExp(exp.id)} style={deleteBtnStyle}>&#10005;</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
          </Card>

          {/* Certifications */}
          <Card>
            <SectionHeader title="Certifications" onAdd={openAddCert} addLabel="Add Certification" />
            {history.certifications.length === 0
              ? <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0', margin: 0 }}>No certifications added yet.</p>
              : history.certifications.map((c) => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.75rem 0', borderBottom: `1px solid ${theme.border}` }}>
                  <div style={{ width: 36, height: 36, borderRadius: '0.5rem', background: '#F0FDF4', border: '1px solid #BBF7D0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>
                    &#127941;
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: '0.875rem', color: theme.text }}>{c.name}</p>
                    <p style={{ margin: '0.15rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>
                      {c.issuing_org && <span>{c.issuing_org}</span>}
                      {c.issue_date && <span> · Issued: {fmt(c.issue_date)}</span>}
                      {c.expiry_date && <span> · Expires: {fmt(c.expiry_date)}</span>}
                    </p>
                  </div>
                  <button onClick={() => deleteCert(c.id)} style={{ ...deleteBtnStyle, flexShrink: 0 }}>&#10005; Delete</button>
                </div>
              ))}
          </Card>

        </div>
      )}

      {/* ══════════════════ MODALS ═══════════════════════════════════════════ */}

      {/* Identity Documents Modal */}
      {modal === 'identity' && (
        <Modal title="Update Identity Documents" onClose={closeModal}>
          <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: '#92400E', background: '#FEF3C7', border: '1px solid #FDE68A', padding: '0.5rem 0.75rem', borderRadius: '0.375rem' }}>
            Changes will be submitted for HR Admin approval before taking effect.
          </p>
          <label style={labelStyle}>Aadhar Number</label>
          <input style={inputStyle} value={identityForm.aadhar_number}
            onChange={e => setIdentityForm(p => ({ ...p, aadhar_number: e.target.value }))}
            placeholder="12-digit Aadhar number" maxLength={12} />
          <label style={labelStyle}>PAN Number</label>
          <input style={inputStyle} value={identityForm.pan_number}
            onChange={e => setIdentityForm(p => ({ ...p, pan_number: e.target.value.toUpperCase() }))}
            placeholder="ABCDE1234F" maxLength={10} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.25rem' }}>
            <button onClick={closeModal} style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', border: `1px solid ${theme.border}`, borderRadius: '0.5rem', background: '#fff', cursor: 'pointer', color: theme.textMuted }}>Cancel</button>
            <button onClick={saveIdentity} disabled={saving} style={{ padding: '0.5rem 1.25rem', fontSize: '0.875rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>{saving ? 'Submitting…' : 'Submit for Approval'}</button>
          </div>
        </Modal>
      )}

      {/* Personal Info Modal */}
      {modal === 'personal' && (
        <Modal title="Request Profile Update" onClose={closeModal}>
          <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: '#92400E', background: '#FEF3C7', border: '1px solid #FDE68A', padding: '0.5rem 0.75rem', borderRadius: '0.375rem' }}>
            Changes will be submitted for HR Admin approval before taking effect.
          </p>
          <Input label="Father / Husband Name" value={personalForm.father_husband_name}
            onChange={e => setPersonalForm(p => ({ ...p, father_husband_name: e.target.value }))} />
          <Input label="Date of Birth" type="date" value={personalForm.dob}
            onChange={e => setPersonalForm(p => ({ ...p, dob: e.target.value }))} />
          <Select label="Gender" value={personalForm.gender}
            onChange={e => setPersonalForm(p => ({ ...p, gender: e.target.value }))}
            options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }]} />
          <Select label="Marital Status" value={personalForm.marital_status}
            onChange={e => setPersonalForm(p => ({ ...p, marital_status: e.target.value }))}
            options={[
              { value: 'single', label: 'Single' }, { value: 'married', label: 'Married' },
              { value: 'divorced', label: 'Divorced' }, { value: 'widowed', label: 'Widowed' },
            ]} />
          <Input label="Blood Group" value={personalForm.blood_group}
            onChange={e => setPersonalForm(p => ({ ...p, blood_group: e.target.value }))} />
          <Input label="Qualification" value={personalForm.qualification}
            onChange={e => setPersonalForm(p => ({ ...p, qualification: e.target.value }))} />
          <Input label="Primary Contact" value={personalForm.phone}
            onChange={e => setPersonalForm(p => ({ ...p, phone: e.target.value }))} />
          <Input label="Alternate Contact" value={personalForm.alternate_phone}
            onChange={e => setPersonalForm(p => ({ ...p, alternate_phone: e.target.value }))} />
          <Input label="Personal Email" type="email" value={personalForm.personal_email}
            onChange={e => setPersonalForm(p => ({ ...p, personal_email: e.target.value }))} />
          <Textarea label="Permanent Address" value={personalForm.address}
            onChange={e => setPersonalForm(p => ({ ...p, address: e.target.value }))} />
          <Textarea label="Present Address" value={personalForm.present_address}
            onChange={e => setPersonalForm(p => ({ ...p, present_address: e.target.value }))} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
            <button onClick={closeModal} style={cancelBtnStyle} disabled={saving}>Cancel</button>
            <button onClick={savePersonal} style={saveBtnStyle} disabled={saving}>{saving ? 'Submitting…' : 'Submit for Approval'}</button>
          </div>
        </Modal>
      )}

      {/* Bank Account Modal */}
      {modal === 'bank' && (
        <Modal title={editingId ? 'Edit Bank Account' : 'Add Bank Account'} onClose={closeModal}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.75rem' }}>
            <div style={{ gridColumn: '1/-1' }}>
              <Input label="Bank Name *" value={bankForm.bank_name}
                onChange={e => setBankForm(p => ({ ...p, bank_name: e.target.value }))} placeholder="e.g. State Bank of India" />
            </div>
            <Input label="Account Number *" value={bankForm.account_number}
              onChange={e => setBankForm(p => ({ ...p, account_number: e.target.value }))} placeholder="Account number" />
            <Input label="IFSC Code" value={bankForm.ifsc_code}
              onChange={e => setBankForm(p => ({ ...p, ifsc_code: e.target.value.toUpperCase() }))} placeholder="e.g. SBIN0001234" />
            <Input label="Branch" value={bankForm.bank_branch}
              onChange={e => setBankForm(p => ({ ...p, bank_branch: e.target.value }))} placeholder="Branch name" />
            <div>
              <label style={labelStyle}>Account Type</label>
              <select value={bankForm.account_type} onChange={e => setBankForm(p => ({ ...p, account_type: e.target.value }))}
                style={{ ...inputStyle }}>
                <option value="savings">Savings</option>
                <option value="current">Current</option>
              </select>
            </div>
            <Input label="Account Holder Name" value={bankForm.account_holder}
              onChange={e => setBankForm(p => ({ ...p, account_holder: e.target.value }))} placeholder="As per bank records" />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginBottom: '0.75rem' }}>
            <input type="checkbox" checked={bankForm.is_primary} onChange={e => setBankForm(p => ({ ...p, is_primary: e.target.checked }))} />
            Set as Primary Account
          </label>
          <ModalActions onCancel={closeModal} onSave={saveBank} saving={saving} />
        </Modal>
      )}

      {/* Nominee Details Modal */}
      {modal === 'nominee' && (
        <Modal title="Edit Nominee Details" onClose={closeModal}>
          <Input label="Nominee Name" value={nomineeForm.nominee_name}
            onChange={e => setNomineeForm(p => ({ ...p, nominee_name: e.target.value }))} />
          <Input label="Relationship" value={nomineeForm.nominee_relation}
            onChange={e => setNomineeForm(p => ({ ...p, nominee_relation: e.target.value }))} />
          <Input label="Nominee Aadhar" value={nomineeForm.nominee_aadhar}
            onChange={e => setNomineeForm(p => ({ ...p, nominee_aadhar: e.target.value }))} />
          <Input label="Nominee Date of Birth" type="date" value={nomineeForm.nominee_dob}
            onChange={e => setNomineeForm(p => ({ ...p, nominee_dob: e.target.value }))} />
          <Input label="Nominee Contact" value={nomineeForm.nominee_contact}
            onChange={e => setNomineeForm(p => ({ ...p, nominee_contact: e.target.value }))} />
          <ModalActions onCancel={closeModal} onSave={saveNominee} saving={saving} />
        </Modal>
      )}

      {/* Education Modal */}
      {modal === 'edu' && (
        <Modal title={editingId ? 'Edit Education' : 'Add Education'} onClose={closeModal}>
          <Input label="Degree / Certificate" value={eduForm.degree}
            onChange={e => setEduForm(p => ({ ...p, degree: e.target.value }))} />
          <Input label="Field of Study" value={eduForm.field_of_study}
            onChange={e => setEduForm(p => ({ ...p, field_of_study: e.target.value }))} />
          <Input label="Institution" value={eduForm.institution}
            onChange={e => setEduForm(p => ({ ...p, institution: e.target.value }))} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Start Year" type="number" value={eduForm.start_year}
              onChange={e => setEduForm(p => ({ ...p, start_year: e.target.value }))} />
            <Input label="End Year" type="number" value={eduForm.end_year}
              onChange={e => setEduForm(p => ({ ...p, end_year: e.target.value }))} />
          </div>
          <Input label="Grade / Percentage" value={eduForm.grade}
            onChange={e => setEduForm(p => ({ ...p, grade: e.target.value }))} />
          <ModalActions onCancel={closeModal} onSave={saveEdu} saving={saving} />
        </Modal>
      )}

      {/* Skill Modal */}
      {modal === 'skill' && (
        <Modal title="Add Skill" onClose={closeModal}>
          <Input label="Skill Name" value={skillForm.skill_name}
            onChange={e => setSkillForm(p => ({ ...p, skill_name: e.target.value }))}
            placeholder="e.g. React, Python, Project Management" />
          <Select label="Proficiency Level" value={skillForm.proficiency_level}
            onChange={e => setSkillForm(p => ({ ...p, proficiency_level: e.target.value }))}
            options={[
              { value: 'beginner', label: 'Beginner' },
              { value: 'intermediate', label: 'Intermediate' },
              { value: 'advanced', label: 'Advanced' },
              { value: 'expert', label: 'Expert' },
            ]} />
          <ModalActions onCancel={closeModal} onSave={saveSkill} saving={saving} />
        </Modal>
      )}

      {/* Work Experience Modal */}
      {modal === 'exp' && (
        <Modal title={editingId ? 'Edit Work Experience' : 'Add Work Experience'} onClose={closeModal}>
          <Input label="Company Name" value={expForm.company_name}
            onChange={e => setExpForm(p => ({ ...p, company_name: e.target.value }))} />
          <Input label="Designation / Role" value={expForm.designation}
            onChange={e => setExpForm(p => ({ ...p, designation: e.target.value }))} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="From Date" type="date" value={expForm.from_date}
              onChange={e => setExpForm(p => ({ ...p, from_date: e.target.value }))} />
            <Input label="To Date (leave blank if current)" type="date" value={expForm.to_date}
              onChange={e => setExpForm(p => ({ ...p, to_date: e.target.value }))} />
          </div>
          <Textarea label="Description" value={expForm.description} rows={3}
            onChange={e => setExpForm(p => ({ ...p, description: e.target.value }))}
            placeholder="Brief description of your role and responsibilities" />
          <ModalActions onCancel={closeModal} onSave={saveExp} saving={saving} />
        </Modal>
      )}

      {/* Certification Modal */}
      {modal === 'cert' && (
        <Modal title="Add Certification" onClose={closeModal}>
          <Input label="Certification Name" value={certForm.name}
            onChange={e => setCertForm(p => ({ ...p, name: e.target.value }))} />
          <Input label="Issuing Organisation" value={certForm.issuing_org}
            onChange={e => setCertForm(p => ({ ...p, issuing_org: e.target.value }))} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Issue Date" type="date" value={certForm.issue_date}
              onChange={e => setCertForm(p => ({ ...p, issue_date: e.target.value }))} />
            <Input label="Expiry Date (optional)" type="date" value={certForm.expiry_date}
              onChange={e => setCertForm(p => ({ ...p, expiry_date: e.target.value }))} />
          </div>
          <ModalActions onCancel={closeModal} onSave={saveCert} saving={saving} />
        </Modal>
      )}

    </div>
  );
}
